import dayjs from "dayjs"
import {Button} from "@progress/kendo-react-buttons"
import {Sparkline, ChartValueAxis, ChartValueAxisItem, ChartArea, ChartTooltip} from "@progress/kendo-react-charts"
import {Field, Form, FormElement, FormRenderProps} from "@progress/kendo-react-form"
import {useInternationalization} from "@progress/kendo-react-intl"
import {ListView, ListViewHeader, ListViewItemProps} from "@progress/kendo-react-listview"
import {MDBRow, MDBCol, MDBSpinner} from "mdbreact"
import {Dispatch, SetStateAction, useEffect, useState} from "react"
import {useDispatch} from "react-redux"
import {Link, useLocation} from "react-router-dom"
import DealerDropdown from "../../components/Shared/dealerDropdown"
import {fetchWeeklyUtilization} from "../../containers/App/appSlice"
import {WeeklyUtilization} from "../../containers/App/models"

import {RootState} from "../../rootReducer"
import {useAppSelector} from "../../store"
import {
    NewBookingParams,
    setNewBookingDetail,
    setSelectedBookingId,
    toggleTestDialog,
} from "../../containers/MotBooking/bookingSlice"
import {setMOTOpportunityDetail, setShowMotOpportunityDialog} from "../../containers/Activity/activitySlice"
import {FollowUpStatus, MotOpportunity} from "../../containers/Activity/models"
import {calltoPreparerDealerName} from "../../utils/common"

const WeeklyUtilizationChart = ({
    showSearchBox,
    focusOnSearch,
}: {
    showSearchBox: Dispatch<SetStateAction<boolean>>
    focusOnSearch: () => void
}) => {
    const dispatch = useDispatch()
    const location = useLocation()
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const userDealers = useAppSelector((state: RootState) => state.application.userDealers)

    const weeklyUtilization = useAppSelector((state: RootState) => state.application.weeklyUtilizationGraph)
    const vehicle = useAppSelector((state: RootState) => state.application.searchedVehicle)
    const [utilizationPage, setUtilizationPage] = useState<number>(0)
    const [utilizationStartDate] = useState<string>(dayjs().startOf("day").format("YYYY-MM-DD"))

    useEffect(() => {
        const dealerId = selectedDealer?.dealerId
        if (dealerId && dealerId > 0) {
            dispatch(fetchWeeklyUtilization(dealerId, utilizationStartDate, utilizationPage))
        }
    }, [dispatch, selectedDealer?.dealerId, utilizationStartDate, utilizationPage])

    const onChangeWeek = (change: string) => {
        if (change === "+") {
            setUtilizationPage(utilizationPage + 1)
        } else if (change === "-") {
            setUtilizationPage(utilizationPage - 1)
        } else {
            setUtilizationPage(0)
        }
        focusOnSearch()
    }

    const WUChartHeader = () => {
        const loading = useAppSelector((state: RootState) => state.application.loadingWeeklyUtilization)
        const weeklyUtilization = useAppSelector((state: RootState) => state.application.weeklyUtilizationGraph)

        return (
            <ListViewHeader style={{color: "rgb(160, 160, 160)", fontSize: 14}}>
                <div>
                    <div className="d-flex justify-content-between align-items-center border-bottom">
                        <h2 style={{fontSize: 12, color: "#434343", marginBottom: 0}} className="font-weight-bold">
                            Untilization
                        </h2>
                        <Button
                            type="button"
                            fillMode="link"
                            themeColor={"dark"}
                            size="medium"
                            title="Refresh"
                            iconClass="fa fa-times mt-1"
                            onClick={() => showSearchBox(false)}
                        ></Button>
                    </div>
                    <Form
                        key={JSON.stringify({
                            saerchDealerDropdown: selectedDealer?.dealerId,
                        })}
                        initialValues={{
                            saerchDealerDropdown: selectedDealer?.dealerId,
                        }}
                        render={(formRenderProps: FormRenderProps) => (
                            <div>
                                <MDBRow className="d-flex justify-content-between align-items-end mb-2">
                                    <MDBCol md="8" sm="12">
                                        <FormElement>
                                            <Field
                                                name={"saerchDealerDropdown"}
                                                component={DealerDropdown}
                                                onChange={focusOnSearch}
                                            />
                                        </FormElement>
                                    </MDBCol>
                                    <MDBCol>
                                        <div>
                                            <Button
                                                type="button"
                                                fillMode="link"
                                                themeColor={"primary"}
                                                size="small"
                                                title="Refresh"
                                                onClick={() => onChangeWeek("")}
                                                disabled={utilizationPage === 0}
                                            >
                                                Today
                                            </Button>
                                            <Button
                                                type="button"
                                                fillMode="link"
                                                themeColor={"primary"}
                                                size="small"
                                                title="Refresh"
                                                iconClass="fa fa-chevron-left mt-1"
                                                className="me-2"
                                                onClick={() => onChangeWeek("-")}
                                                disabled={utilizationPage === 0}
                                            ></Button>
                                            <Button
                                                type="button"
                                                fillMode="link"
                                                themeColor={"primary"}
                                                size="small"
                                                title="Refresh"
                                                iconClass="fa fa-chevron-right mt-1"
                                                onClick={() => onChangeWeek("+")}
                                            ></Button>
                                        </div>
                                    </MDBCol>
                                </MDBRow>
                            </div>
                        )}
                    />
                    <div className="w-100 d-flex justify-content-around mb-2">
                        {loading && (
                            <MDBSpinner role="status">
                                <span className="visually-hidden">Loading...</span>
                            </MDBSpinner>
                        )}
                        {!loading && weeklyUtilization.length === 0 && (
                            <div style={{fontSize: 12, color: "#a0a0a0"}}>No data found</div>
                        )}
                    </div>
                </div>
            </ListViewHeader>
        )
    }

    const WUChartItemRender = (props: ListViewItemProps) => {
        const intl = useInternationalization()
        let item = props.dataItem as WeeklyUtilization
        const bulletData = [item.bookedMins]
        const charLineColor: string[] = [item.bookedPerc > 100 ? "#f93154" : "#9c27b0"]

        const bulletValueAxis = {
            min: 0,
            max: item.totalMins,
            background: "red",
            plotBands: [
                {
                    from: 0,
                    to: item.totalMins,
                    color: "#787878",
                    opacity: 0.1,
                },
            ],
        }

        const toolTipRender = () => {
            return (
                <div>
                    <div className="w-100">Current : {item.bookedMins} Mins</div>
                    <div className="w-100">Target : {item.totalMins} Mins</div>
                    <div className="w-100"> {item.bookedPerc} %</div>
                </div>
            )
        }

        return (
            <div className="d-flex p-1">
                <Link
                    to={`${location.pathname}`}
                    onClick={e => {
                        e.preventDefault()
                        const selectedPreparerDealerName = calltoPreparerDealerName(
                            userDealers,
                            selectedDealer?.dealerId,
                        )

                        let params = {
                            dealerId: selectedDealer?.dealerId,
                            testStart: dayjs(item.availabilityStart).startOf("date").toDate().toString(),
                            testEnd: dayjs(item.availabilityStart).startOf("date").toDate().toString(),
                            testPackageID: 0,
                            registration: vehicle?.registration ?? "",
                            customer: null,
                            preparerDealerName:
                                selectedPreparerDealerName !== undefined ? selectedPreparerDealerName : null,
                            preparedByDealer: selectedPreparerDealerName !== undefined ? true : false,
                        } as NewBookingParams
                        dispatch(setNewBookingDetail(params))
                        dispatch(setSelectedBookingId(0))
                        toggleBookingDialog(true)
                        showSearchBox(false)
                    }}
                >
                    <div
                        className="d-flex justify-content-start"
                        style={{textDecoration: "underline", cursor: "pointer", width: "120px"}}
                    >
                        {intl.formatDate(dayjs(item.utDate).toDate(), {weekday: "short"})} &nbsp;
                        {intl.formatDate(dayjs(item.utDate).toDate(), {date: "short"})}
                    </div>
                </Link>
                <Sparkline seriesColors={charLineColor} data={bulletData} type="bullet">
                    <ChartArea width={360} height={25} />
                    <ChartValueAxis>
                        <ChartValueAxisItem {...bulletValueAxis} />
                    </ChartValueAxis>
                    <ChartTooltip render={toolTipRender} />
                </Sparkline>
                <div className="ms-2" style={{width: "120px"}}>
                    {item.available}&nbsp; Hrs avail{" "}
                </div>
            </div>
        )
    }

    const toggleBookingDialog = (state: boolean) => {
        dispatch(toggleTestDialog(state))
    }

    const openAddToOpportunityDialog = () => {
        const data = {} as MotOpportunity
        if (selectedDealer && vehicle) {
            data.dealerID = selectedDealer?.dealerId
            data.vehicleID = vehicle?.vehicleId
            data.registration = vehicle?.registration
            data.motExpiryDate =
                vehicle?.annualTestExpiryDate !== null ? dayjs(vehicle?.annualTestExpiryDate).toDate() : null
            data.followUpStatus = FollowUpStatus[0]
            dispatch(setShowMotOpportunityDialog(true))
            dispatch(setMOTOpportunityDetail(data))

            showSearchBox(false)
        }
    }

    return (
        <>
            <ListView
                data={weeklyUtilization}
                item={WUChartItemRender}
                style={{width: "100%"}}
                header={WUChartHeader}
            />

            {selectedDealer && vehicle && vehicle !== null && (
                <div className="w-100 d-flex justify-content-end mb-2 mt-2">
                    <Button
                        type="button"
                        fillMode="flat"
                        themeColor={"primary"}
                        size="small"
                        title="AddToOpportunity"
                        iconClass="fa fa-plus"
                        onClick={openAddToOpportunityDialog}
                    >
                        Add To Opportunity
                    </Button>
                </div>
            )}
        </>
    )
}

export default WeeklyUtilizationChart
